import { useMemo, useState } from 'react';
import { IconArrowLeft, IconChartBar, IconDots, IconMap, IconPlus, IconQrcode, IconSearch, IconTruck, IconX } from '@tabler/icons-react';
import { DataError, useList } from '@hana/data';
import { useNavigate } from '@tanstack/react-router';
import { Page, PageHeader } from '@hana/shell';
import { Button, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { useWide } from './useWide';
import { GPS_LABEL, GPS_TONE, PIN_POS, EXPIRING, filterVehicles, fleetKpis, gpsSlices, isTruck, moocFoot, type FleetFilter } from './fleet';
import type { Vehicle } from './types';
import { useT } from '@hana/i18n';
import './i18n';
import s from './fleet.module.css';

// Mint-TMS-Fleet (desktop 1536) · tab "Xe & mooc". Chỉ đọc; nút ghi báo toast.
// Chưa làm: tab Bảo dưỡng / Trạm thu phí & ePass / Giá dầu / Nhập sao kê, ngăn chi tiết xe, thêm xe. Bản đồ là khung giữ chỗ (chưa nối VietMap).

type Tab = 'veh' | 'mnt' | 'toll' | 'fuel' | 'imp';
const TABS: ReadonlyArray<{ key: Tab; label: string; n?: number }> = [
  { key: 'veh', label: 'Xe & mooc', n: 15 },
  { key: 'mnt', label: 'Bảo dưỡng', n: 3 },
  { key: 'toll', label: 'Trạm thu phí & ePass', n: 207 },
  { key: 'fuel', label: 'Giá dầu', n: 171 },
  { key: 'imp', label: 'Nhập sao kê' },
];

const MAP_FILTERS: ReadonlyArray<{ key: FleetFilter; label: string }> = [
  { key: 'all', label: 'Tất cả' }, { key: 'run', label: 'Đang chạy' }, { key: 'idle', label: 'Đứng chờ' }, { key: 'stop', label: 'Dừng' }, { key: 'lost', label: 'Mất GPS' },
];
const TABLE_FILTERS: ReadonlyArray<{ key: FleetFilter; label: string }> = [{ key: 'all', label: 'Tất cả' }, { key: 'Truck', label: 'Đầu kéo' }, { key: 'Mooc', label: 'Mooc' }];
const GPS_VAR = { run: 'var(--d-green)', idle: 'var(--d-amber)', stop: 'var(--d-gray)', lost: 'var(--err)' } as const;
const R = 52;
const C = 2 * Math.PI * R;

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

const ALERTS: ReadonlyArray<{ k: string; v: string; c: string; strong?: boolean; ak?: string }> = [
  { k: '50H60625 · khoá kỹ thuật', v: 'Quá hạn BD2 3.330 km', c: 'var(--err)', strong: true, ak: 'Đã làm' },
  { k: '50H60625 · đăng kiểm 03/11/2026', v: 'còn 11 ngày', c: 'var(--d-amber)', strong: true },
  { k: '50RM05007 · lưu tại Kho lạnh NECS 52 giờ', v: 'quá 24 giờ · tính phí lưu mooc', c: 'var(--d-amber)' },
  { k: '50E13970 · tụt nhiên liệu 38 lít', v: '19/10 02:14 · Bãi xe COV', c: 'var(--d-amber)' },
  { k: '50RM05007 · BDM1 đến hạn', v: '25/10/2026', c: 'var(--lb)' },
];

function FleetDock({ list, onClose, onFilter, onWrite }: { list: readonly Vehicle[]; onClose: () => void; onFilter: (f: FleetFilter) => void; onWrite: () => void }) {
  const slices = gpsSlices(list);
  const t = useT();
  const trucks = list.filter(isTruck);
  let acc = 0;
  return (
    <div className={s.dock} aria-label="Tổng quan xe">
      <div className={s.dkHd}>
        <b>{t('tms.fleetOverview')}</b>
        <button type="button" className={s.dkX} aria-label="Đóng bảng" onClick={onClose}><IconX size={17} aria-hidden="true" /></button>
      </div>
      <div className={s.dkKpis}>
        {fleetKpis(list).map((k) => (
          <button key={k.key} type="button" className={s.dkKpi} aria-label={`${k.label} ${k.value} ${k.unit}`} onClick={() => (k.key === 'run' ? onFilter('run') : k.key === 'own' ? onFilter('all') : onWrite())}>
            <div className={s.dkKl}>{k.label}</div>
            <div className={s.dkKv}><b className={k.tone ? s[k.tone] : ''}>{k.value}</b><span>{k.unit}</span></div>
          </button>
        ))}
      </div>
      <section aria-label="Xe hoạt động" className={s.dkChart}>
        <div className={s.cardHd}><h2 className={s.dkH2}>{t('tms.activeVehicles')}</h2><span className={s.note}>{`${trucks.length} đầu kéo · GPS`}</span></div>
        <div className={s.donutRow}>
          <svg role="img" aria-label={`Đầu kéo theo GPS: ${slices.map((x) => `${x.label} ${x.n}`).join(', ')}`} width="96" height="96" viewBox="0 0 132 132">
            <circle cx="66" cy="66" r={R} fill="none" stroke="var(--line)" strokeWidth="16" />
            {slices.map((x) => {
              const len = (x.n / (trucks.length || 1)) * C;
              const el = <circle key={x.key} cx="66" cy="66" r={R} fill="none" stroke={GPS_VAR[x.key]} strokeWidth="16" strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-acc} transform="rotate(-90 66 66)" />;
              acc += len;
              return x.n ? el : null;
            })}
            <text x="66" y="68" textAnchor="middle" fontSize="22" fontWeight="550" fill="var(--tx)">{trucks.length}</text>
            <text x="66" y="85" textAnchor="middle" fontSize="11" fill="var(--lb)">{t('tms.tractors')}</text>
          </svg>
          <div className={s.legend}>
            {slices.map((x) => (
              <button key={x.key} type="button" className={s.lg} onClick={() => onFilter(x.key)}>
                <i style={{ background: GPS_VAR[x.key] }} /><span>{x.label}</span><b>{x.n}</b><em>{`${x.pct}%`}</em>
              </button>
            ))}
          </div>
        </div>
        <div className={s.foot2}>{moocFoot(list)}</div>
      </section>
      <div className={s.dkSub}>{t('tms.alerts')}</div>
      {ALERTS.map((n) => (
        <div key={n.k} className={s.alert}>
          <span className={s.alDot} style={{ background: n.c }} />
          <div className={s.alBody}><div style={{ fontWeight: n.strong ? 550 : 400 }}>{n.k}</div><div className={s.alV}>{n.v}</div></div>
          {n.ak && <button type="button" className={s.alAct} onClick={onWrite}>{n.ak}</button>}
        </div>
      ))}
    </div>
  );
}

function VehicleTab({ list, f, setF, q, setQ }: { list: readonly Vehicle[]; f: FleetFilter; setF: (f: FleetFilter) => void; q: string; setQ: (q: string) => void }) {
  const toast = useToast();
  const t = useT();
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  const rows = useMemo(() => filterVehicles(list, f, q), [list, f, q]);
  const trucks = list.filter(isTruck);

  return (
    <div className={s.split}>
      <section aria-label="Danh sách xe & mooc" className={`${s.card} ${s.cards}`}>
        <div className={s.cHd}>
          <div><h2 className={s.h2}>{t('tms.vehicleList')}</h2><div className={s.note}>{`${rows.length} / ${list.length} xe`}</div></div>
          <button type="button" className={s.qr} aria-label="In QR mooc" title="In QR mooc" onClick={() => toast({ msg: 'Đã tạo PDF 8 nhãn QR mooc · khổ A6' })}><IconQrcode size={16} aria-hidden="true" /></button>
        </div>
        <div className={s.cTools}>
          <label className={s.search}>
            <IconSearch size={14} stroke={2.2} aria-hidden="true" />
            <input aria-label="Tìm biển số, vị trí, chuyến" placeholder="Tìm biển số, vị trí, chuyến" value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          <div role="group" aria-label="Lọc" className={s.seg}>
            {TABLE_FILTERS.map((m) => (
              <button key={m.key} type="button" aria-pressed={f === m.key} className={`${s.segB} ${f === m.key ? s.segOn : ''}`} onClick={() => setF(m.key)}>{m.label}</button>
            ))}
          </div>
        </div>
        <div role="list" aria-label="Danh sách xe & mooc" className={s.vList}>
          {rows.length === 0 && <div className={s.none}>{`Không có xe khớp "${q}" và bộ lọc.`}</div>}
          {rows.map((v) => {
            const tone = v.locked ? 'err' : GPS_TONE[v.gps];
            const lost = v.tripNote.includes('lưu');
            return (
              <button key={v.plate} type="button" role="listitem" className={s.vCard} aria-label={`Mở ${v.plate}`} onClick={noWrite}>
                <div className={s.vTop}>
                  <b>{v.plate}</b>
                  <span className={s.vTy}>{`${v.kind === 'Truck' ? 'Đầu kéo' : 'Mooc'} · ${v.model.split(' · ')[0]}`}</span>
                  <span className={`${s.lv} ${s[`lv_${tone}`]}`}><i className={s[`dot_${tone}`]} />{v.locked ? 'Khoá kỹ thuật' : GPS_LABEL[v.gps]}</span>
                </div>
                <div className={s.vLoc}>{v.location}</div>
                <div className={s.vBot}>
                  <span className={v.tripId ? s.trip : lost ? s.warnTx : s.lbTx}>{v.tripId ?? (v.tripNote === '—' ? 'Chưa gán chuyến' : v.tripNote)}</span>
                  <span className={s.grow} />
                  <span className={s.lbTx}>{`ODO ${v.odo}`}</span>
                  <span title="Đăng kiểm" className={v.inspectionDue === EXPIRING ? s.warnTx : s.tx2}>{`ĐK ${v.inspectionDue}`}</span>
                </div>
              </button>
            );
          })}
        </div>
        <div className={s.note}>{t('tms.fleetFoot')}</div>
      </section>
      <section aria-label="Xe trên bản đồ · VietMap" className={`${s.card} ${s.mapCard}`}>
        <div className={s.cardHd}>
          <h2 className={s.h2}>{t('tms.mapVehicles')}</h2>
          <span className={s.mapCtl}>
            {MAP_FILTERS.map((m) => (
              <button key={m.key} type="button" aria-pressed={f === m.key} className={`${s.chip} ${f === m.key ? s.chipOn : ''}`} onClick={() => setF(m.key)}>{m.label}</button>
            ))}
          </span>
        </div>
        <div role="img" aria-label={`Khung bản đồ giữ chỗ, ${trucks.length} đầu kéo`} className={s.map}>
          <svg aria-hidden="true" width="100%" height="100%" viewBox="0 0 800 230" preserveAspectRatio="none">
            <path d="M0 70 C 200 60, 340 110, 800 90" className={s.road} strokeWidth="7" />
            <path d="M160 230 C 220 170, 420 140, 560 0" className={s.road} strokeWidth="5" />
            <path d="M0 190 C 300 170, 520 200, 800 160" className={s.road} strokeWidth="4" />
          </svg>
          {trucks.filter((v) => f === 'all' || f === 'Truck' || v.gps === f).map((v) => {
            const pos = PIN_POS[v.plate];
            return pos && v.gps !== 'na' ? <span key={v.plate} className={s.pin} title={`${v.plate} · ${GPS_LABEL[v.gps]}`} style={{ left: pos[0], top: pos[1], background: GPS_VAR[v.gps] }} /> : null;
          })}
          <span className={s.mapLb} style={{ left: '18%', top: '30%' }}>{t('tms.catLai')}</span>
          <span className={s.mapLb} style={{ left: '46%', top: '42%' }}>{t('tms.trungLuong')}</span>
          <span className={s.mapLb} style={{ left: '62%', top: '76%' }}>{t('tms.vinhLong')}</span>
        </div>
        <div className={s.mapLg}>
          {([['Đang chạy', 'run'], ['Đứng chờ (nổ máy)', 'idle'], ['Dừng', 'stop'], ['Mất GPS', 'lost']] as const).map(([k, g]) => (
            <span key={g}><i style={{ background: GPS_VAR[g] }} />{k}</span>
          ))}
        </div>
      </section>
    </div>
  );
}

export function FleetPage() {
  const toast = useToast();
  const t = useT();
  const navigate = useNavigate();
  const { data, error, isLoading, isError, refetch } = useList('vehicle', { limit: 500 });
  const forced = readState();
  const [tab, setTab] = useState<Tab>('veh');
  const wide = useWide();
  const [f, setF] = useState<FleetFilter>('all');
  const [q, setQ] = useState('');
  const [dockOn, setDockOn] = useState(false);
  const [dockX, setDockX] = useState(false);
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  const list = data?.items ?? [];
  const trucks = list.filter(isTruck);
  const locked = list.filter((v) => v.locked).length;

  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (isLoading) state = 'loading';
  else if (isError) state = error instanceof DataError && error.code === 'noperm' ? 'noperm' : 'error';
  else if (list.length === 0) state = 'empty';

  const dockOpen = tab === 'veh' && state === 'ready' && (wide ? !dockX : dockOn);
  const closeDock = () => (wide ? setDockX(true) : setDockOn(false));
  const toggleDock = () => (wide ? setDockX(!dockX) : setDockOn(!dockOn));

  return (
    <Page kind="table" dock={dockOpen ? <FleetDock list={list} onClose={closeDock} onFilter={setF} onWrite={noWrite} /> : undefined}>
      <PageHeader
        title="Phương tiện & danh mục"
        lead={
          <button type="button" className={s.back} aria-label="Về Vận tải" title="Về Vận tải" onClick={() => void navigate({ to: '/ezy/tms' })}>
            <IconArrowLeft size={18} aria-hidden="true" />
          </button>
        }
        tabs={
          <span className={s.hChips}>
            <span className={`${s.hChip} ${s.hBlue}`}><i style={{ background: 'var(--d-blue)' }} />{`${trucks.length} đầu kéo · ${list.length - trucks.length} mooc`}</span>
            {locked > 0 && <span className={`${s.hChip} ${s.hErr}`}><i style={{ background: 'var(--err)' }} />{`${locked} xe khoá kỹ thuật`}</span>}
          </span>
        }
        actions={
          <>
            <Segmented kind="toggle" label="Mật độ" value="std" onChange={noWrite} items={[{ key: 'std', label: 'Chuẩn' }, { key: 'compact', label: 'Gọn' }]} />
            {tab === 'veh' && !wide && (
              <button type="button" className={`${s.more} ${dockOn ? s.moreOn : ''}`} aria-label="Tổng quan xe · 5 cảnh báo" title="Tổng quan xe · 5 cảnh báo" aria-pressed={dockOn} onClick={toggleDock}>
                <IconChartBar size={18} aria-hidden="true" />
              </button>
            )}
            <Button variant="primary" icon={<IconPlus size={16} />} onClick={noWrite}>{t('tms.addVehicle')}</Button>
            <button type="button" className={s.more} aria-label="Thêm thao tác" title="Thêm thao tác" onClick={noWrite}>
              <IconDots size={18} aria-hidden="true" />
            </button>
          </>
        }
      />
      <div className={s.page}>
        <div className={s.sub}>{`${list.length} xe · ${list.length - trucks.length} mooc`}</div>
        <div className={s.tabRow}>
          <Segmented label="Mục phương tiện" value={tab} onChange={setTab} items={TABS.map((t) => ({ key: t.key, label: t.label, n: t.n }))} />
          <Button variant="soft" icon={<IconMap size={16} />} onClick={() => void navigate({ to: '/ezy/tms/places' })}>{t('tms.placesMap')}</Button>
        </div>
        <StateView
          state={tab === 'veh' ? state : 'empty'}
          icon={<IconTruck size={26} stroke={1.8} />}
          title={tab !== 'veh' ? 'Mục này chưa mở' : state === 'empty' ? 'Chưa có xe nào' : undefined}
          action={state === 'error' ? <Button variant="secondary" onClick={() => void refetch()}>{t('common.retry')}</Button> : undefined}
          skeleton={
            <>
              <div className={s.split}>
                <div className={s.skel} style={{ flex: '0 0 380px', padding: 18, height: 640 }}><Skeleton /></div>
                <div className={s.skel} style={{ flex: '1 1 0', height: 640 }} />
              </div>
            </>
          }
        >
          <VehicleTab list={list} f={f} setF={setF} q={q} setQ={setQ} />
        </StateView>
      </div>
    </Page>
  );
}
